import React, { useState, useEffect, useCallback } from 'react';
import instance from '../api/Axios';

const BMOUserPermissionAssignment = () => {
    const [bmoId, setBmoId] = useState(null);
    const [users, setUsers] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [groupedPermissions, setGroupedPermissions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userPermissions, setUserPermissions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [userTypeFilter, setUserTypeFilter] = useState('');
    const [viewMode, setViewMode] = useState('templates'); // 'templates' or 'permissions'
    const [pendingPermissionChanges, setPendingPermissionChanges] = useState([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertData, setAlertData] = useState({ type: '', title: '', message: '' });
    const [showUserModal, setShowUserModal] = useState(false);
    const [modalUser, setModalUser] = useState(null);
    
    // Fund assignment state
    const [userFunds, setUserFunds] = useState({});
    const [availableFunds, setAvailableFunds] = useState([]);
    const [selectedFunds, setSelectedFunds] = useState([]);
    const [showFundModal, setShowFundModal] = useState(false);
    const [fundAssignmentLoading, setFundAssignmentLoading] = useState(false);

    // Fetch BMO ID from dashboard API
    const fetchBMOId = useCallback(async () => {
        try {
            const response = await instance.get('/api/accounts/bmo-user-admin/dashboard/');
            if (response.data.bmoInfo) {
                setBmoId(response.data.bmoInfo.id);
            }
        } catch (error) {
            console.error('Error fetching BMO ID:', error);
        }
    }, []);

    const loadUsers = useCallback(async () => {
        try {
            if (!bmoId) {
                console.log('No BMO ID available yet');
                return;
            }

            const params = {
                bmo: bmoId // Filter by the BMO admin's BMO
            };
            
            if (searchTerm) params.search = searchTerm;
            if (userTypeFilter) params.user_type = userTypeFilter;

            const response = await instance.get('/api/accounts/user-permissions/', { params });
            setUsers(response.data.results || response.data);
        } catch (error) {
            console.error('Error loading users:', error);
        }
    }, [searchTerm, userTypeFilter, bmoId]);

    const loadTemplates = useCallback(async () => {
        try {
            const response = await instance.get('/api/accounts/permission-templates/');
            setTemplates(response.data.results || response.data);
        } catch (error) {
            console.error('Error loading templates:', error);
        }
    }, []);

    const loadGroupedPermissions = useCallback(async () => {
        try {
            const response = await instance.get('/api/accounts/permissions/grouped/');
            setGroupedPermissions(response.data);
        } catch (error) {
            console.error('Error loading grouped permissions:', error);
        }
    }, []);

    // Fund assignment functions
    const loadUserFunds = useCallback(async (userId) => {
        if (!userId) return;
        
        try {
            const response = await instance.get(`/api/accounts/user-fund-assignments/${userId}/restricted_funds/`);
            setUserFunds(prev => ({
                ...prev,
                [userId]: response.data.restricted_funds || []
            }));
        } catch (error) {
            console.error('Error loading user funds:', error);
        }
    }, []);

    const loadUserPermissions = useCallback(async (userId) => {
        if (!userId) return;
        
        setLoading(true);
        try {
            const response = await instance.get(`/api/accounts/user-permissions/${userId}/`);
            console.log('🔍 DEBUGGING USER PERMISSIONS FOR:', response.data.email);
            console.log('📋 Individual permissions:', response.data.individual_permissions);
            console.log('📋 Template permissions:', response.data.permission_templates);
            console.log('📋 Effective permissions:', response.data.effective_permissions);
            setUserPermissions(response.data);
            setPendingPermissionChanges([]);
            setHasUnsavedChanges(false);
            
            // Load user fund assignments
            loadUserFunds(userId);
        } catch (error) {
            console.error('Error loading user permissions:', error);
        } finally {
            setLoading(false);
        }
    }, [loadUserFunds]);

    const loadAvailableFunds = useCallback(async () => {
        try {
            // Use the new available_funds endpoint for BMO admins
            const response = await instance.get('/api/accounts/user-fund-assignments/available_funds/');
            console.log('Available funds response:', response.data);
            setAvailableFunds(response.data.available_funds || []);
        } catch (error) {
            console.error('Error loading available funds:', error);
            // Show user-friendly error
            setAlertData({
                type: 'warning',
                title: 'Unable to Load Funds',
                message: `Cannot load available funds: ${error.response?.data?.error || error.message}. You may not have permission to view funds or there may be no funds available in your BMO.`
            });
            setShowAlert(true);
            setAvailableFunds([]);
        }
    }, []);

    const handleFundAssignment = async (userId, fundIds, action = 'assign') => {
        setFundAssignmentLoading(true);
        try {
            const endpoint = action === 'assign' ? 'assign_funds' : 'remove_funds';
            await instance.post(`/api/accounts/user-fund-assignments/${userId}/${endpoint}/`, {
                fund_ids: fundIds
            });
            
            // Reload user funds
            await loadUserFunds(userId);
            
            setAlertData({
                type: 'success',
                title: 'Success',
                message: `Successfully ${action}ed ${fundIds.length} fund(s)`
            });
            setShowAlert(true);
            
        } catch (error) {
            console.error(`Error ${action}ing funds:`, error);
            setAlertData({
                type: 'danger',
                title: 'Error',
                message: `Failed to ${action} funds: ${error.response?.data?.error || error.message}`
            });
            setShowAlert(true);
        } finally {
            setFundAssignmentLoading(false);
        }
    };

    const openFundModal = (user) => {
        setSelectedUser(user);
        setSelectedFunds([]);
        loadAvailableFunds();
        setShowFundModal(true);
    };

    const handleFundSelection = (fundId, isSelected) => {
        if (isSelected) {
            setSelectedFunds(prev => [...prev, fundId]);
        } else {
            setSelectedFunds(prev => prev.filter(id => id !== fundId));
        }
    };

    const assignSelectedFunds = async () => {
        if (!selectedUser || selectedFunds.length === 0) return;
        
        await handleFundAssignment(selectedUser.id, selectedFunds, 'assign');
        setShowFundModal(false);
        setSelectedFunds([]);
    };

    const removeFund = async (userId, fundId) => {
        await handleFundAssignment(userId, [fundId], 'remove');
    };

    // Test email function
    const testEmail = async () => {
        try {
            await instance.post('/api/accounts/test-email/', {
                email: 'admin@example.com'
            });
            
            setAlertData({
                type: 'success',
                title: 'Email Test',
                message: 'Test email sent successfully! Check the Django Debug Toolbar for the email details.'
            });
            setShowAlert(true);
        } catch (error) {
            console.error('Email test error:', error);
            setAlertData({
                type: 'error',
                title: 'Email Test Failed',
                message: `Failed to send test email: ${error.message}`
            });
            setShowAlert(true);
        }
    };

    const handleUserSelect = useCallback((user) => {
        if (hasUnsavedChanges) {
            const confirm = window.confirm('You have unsaved changes. Are you sure you want to switch users?');
            if (!confirm) return;
        }
        setSelectedUser(user);
        loadUserPermissions(user.id);
    }, [hasUnsavedChanges, loadUserPermissions]);

    const handleViewUser = useCallback(async (user) => {
        try {
            // Fetch comprehensive user details from the new endpoint
            const userResponse = await instance.get(`/api/accounts/users/${user.id}/`);
            const userData = userResponse.data;
            
            console.log('Complete user data from new API:', userData);
            
            setModalUser(userData);
            setShowUserModal(true);
        } catch (error) {
            console.error('Error loading user details:', error);
            // Fallback to basic user data if fetch fails
            setModalUser(user);
            setShowUserModal(true);
        }
    }, []);

    const handleTemplateToggle = useCallback((templateId) => {
        if (!selectedUser || loading) return;

        const currentTemplates = userPermissions?.permission_templates || [];
        const hasTemplate = currentTemplates.some(t => t.id === templateId);
        
        let newChanges;
        if (hasTemplate) {
            newChanges = pendingPermissionChanges.filter(change => 
                !(change.type === 'template' && change.template_id === templateId && change.action === 'add')
            );
            const existingRemove = newChanges.find(change => 
                change.type === 'template' && change.template_id === templateId && change.action === 'remove'
            );
            if (!existingRemove) {
                newChanges.push({
                    type: 'template',
                    template_id: templateId,
                    action: 'remove'
                });
            }
        } else {
            newChanges = pendingPermissionChanges.filter(change => 
                !(change.type === 'template' && change.template_id === templateId && change.action === 'remove')
            );
            const existingAdd = newChanges.find(change => 
                change.type === 'template' && change.template_id === templateId && change.action === 'add'
            );
            if (!existingAdd) {
                newChanges.push({
                    type: 'template',
                    template_id: templateId,
                    action: 'add'
                });
            }
        }
        
        setPendingPermissionChanges(newChanges);
        setHasUnsavedChanges(newChanges.length > 0);
    }, [selectedUser, userPermissions, loading, pendingPermissionChanges]);

    const handlePermissionToggle = useCallback((permissionId) => {
        if (!selectedUser || loading) return;

        // Check direct individual permissions
        const currentPermissions = userPermissions?.individual_permissions || [];
        const hasDirectPermission = currentPermissions.some(p => p.id === permissionId);
        
        // Check permissions from templates (effective permissions)
        const effectivePermissions = userPermissions?.effective_permissions || [];
        const hasTemplatePermission = effectivePermissions.some(p => p.id === permissionId);
        
        // Check which templates provide this permission
        const templatesWithPermission = (userPermissions?.permission_templates || []).filter(template => 
            template.permissions.some(p => p.id === permissionId)
        );
        
        // If user has this permission from templates but NOT from direct assignment,
        // and they're trying to toggle it off, prevent the action
        if (hasTemplatePermission && !hasDirectPermission && templatesWithPermission.length > 0) {
            const templateNames = templatesWithPermission.map(t => t.name).join(', ');
            
            setAlertData({
                type: 'warning',
                title: 'Cannot Remove Group Permission',
                message: `This permission comes from the group: "${templateNames}". Remove the user from these groups first to remove the permission.`
            });
            setShowAlert(true);
            return;
        }
        
        // Determine if user currently has the permission (from any source)
        const hasAnyPermission = hasDirectPermission || hasTemplatePermission;
        
        let newChanges;
        if (hasAnyPermission) {
            // If user currently has the permission (directly or through template)
            newChanges = pendingPermissionChanges.filter(change => 
                !(change.type === 'permission' && change.permission_id === permissionId && change.action === 'add')
            );
            const existingRemove = newChanges.find(change => 
                change.type === 'permission' && change.permission_id === permissionId && change.action === 'remove'
            );
            if (!existingRemove) {
                newChanges.push({
                    type: 'permission',
                    permission_id: permissionId,
                    action: 'remove'
                });
            }
        } else {
            // If user doesn't have the permission, add it
            newChanges = pendingPermissionChanges.filter(change => 
                !(change.type === 'permission' && change.permission_id === permissionId && change.action === 'remove')
            );
            const existingAdd = newChanges.find(change => 
                change.type === 'permission' && change.permission_id === permissionId && change.action === 'add'
            );
            if (!existingAdd) {
                newChanges.push({
                    type: 'permission',
                    permission_id: permissionId,
                    action: 'add'
                });
            }
        }
        
        setPendingPermissionChanges(newChanges);
        setHasUnsavedChanges(newChanges.length > 0);
    }, [selectedUser, userPermissions, loading, pendingPermissionChanges]);

    const saveChanges = useCallback(async () => {
        if (!selectedUser || pendingPermissionChanges.length === 0) return;

        setLoading(true);
        try {
            const payload = {
                user_id: selectedUser.id,
                changes: pendingPermissionChanges
            };

            console.log('Sending bulk update payload:', payload);
            console.log('Pending changes details:', pendingPermissionChanges);

            await instance.post('/api/accounts/user-permissions/bulk_update/', payload);
            
            setAlertData({
                type: 'success',
                title: 'Success!',
                message: 'User permissions updated successfully.'
            });
            setShowAlert(true);
            
            // Reload user permissions
            await loadUserPermissions(selectedUser.id);
            await loadUsers(); // Refresh user list to show updated template counts
            
        } catch (error) {
            console.error('Error saving changes:', error);
            setAlertData({
                type: 'error',
                title: 'Error',
                message: error.response?.data?.detail || 'Failed to update permissions.'
            });
            setShowAlert(true);
        } finally {
            setLoading(false);
        }
    }, [selectedUser, pendingPermissionChanges, loadUserPermissions, loadUsers]);

    const discardChanges = useCallback(() => {
        setPendingPermissionChanges([]);
        setHasUnsavedChanges(false);
        if (selectedUser) {
            loadUserPermissions(selectedUser.id);
        }
    }, [selectedUser, loadUserPermissions]);

    useEffect(() => {
        fetchBMOId();
    }, [fetchBMOId]);

    useEffect(() => {
        if (bmoId) {
            loadUsers();
            loadTemplates();
            loadGroupedPermissions();
        }
    }, [loadUsers, loadTemplates, loadGroupedPermissions, viewMode, bmoId]);

    const getTemplateStatus = (templateId) => {
        const currentTemplates = userPermissions?.permission_templates || [];
        const hasTemplate = currentTemplates.some(t => t.id === templateId);
        const pendingChange = pendingPermissionChanges.find(change => 
            change.type === 'template' && change.template_id === templateId
        );
        
        if (pendingChange) {
            // If pending add, show as checked; if pending remove, show as unchecked
            return pendingChange.action === 'add' ? true : false;
        }
        return hasTemplate;
    };



    const getPermissionStatus = useCallback((permissionId) => {
        // Check direct individual permissions
        const currentPermissions = userPermissions?.individual_permissions || [];
        const hasDirectPermission = currentPermissions.some(p => p.id === permissionId);
        
        // Check permissions from templates (effective permissions)
        const effectivePermissions = userPermissions?.effective_permissions || [];
        const hasTemplatePermission = effectivePermissions.some(p => p.id === permissionId);
        
        // User has the permission if they have it directly OR through templates
        const hasPermission = hasDirectPermission || hasTemplatePermission;
        
        const pendingChange = pendingPermissionChanges.find(change => 
            change.type === 'permission' && change.permission_id === permissionId
        );
        
        // Debug logging for specific problem permissions
        const allPermissions = groupedPermissions.flatMap(group => group.permissions);
        const permission = allPermissions.find(p => p.id === permissionId);
        const codename = permission?.codename || permission?.name?.toLowerCase();
        
        if (codename === 'add_funderofficer') {
            console.log(`🔍 getPermissionStatus for add_funderofficer (${permissionId}):`, {
                hasDirectPermission,
                hasTemplatePermission,
                hasPermission,
                pendingChange: pendingChange ? `${pendingChange.action}` : 'none',
                finalResult: pendingChange ? (pendingChange.action === 'add' ? true : false) : hasPermission
            });
        }
        
        if (pendingChange) {
            // If pending add, show as checked; if pending remove, show as unchecked
            return pendingChange.action === 'add' ? true : false;
        }
        
        return hasPermission;
    }, [userPermissions, pendingPermissionChanges, groupedPermissions]);







    // Unified funder toggle handler that manages all funder-related permissions together
    const handleUnifiedFunderToggle = useCallback((action, funderPerms, allFunderPermissions) => {
        if (!selectedUser || loading) return;

        // Find all permissions for this action
        const funderPerm = funderPerms.find(p => 
            (p.codename || p.name?.toLowerCase()) === `${action}_funder`
        );
        const repPerm = allFunderPermissions.find(p => 
            (p.codename || p.name?.toLowerCase()) === `${action}_funderprimaryrepresentative`
        );
        const officerPerm = allFunderPermissions.find(p => 
            (p.codename || p.name?.toLowerCase()) === `${action}_funderofficer`
        );
        
        // Get current state of funder permission (this will be the master)
        const currentState = funderPerm ? getPermissionStatus(funderPerm.id) : false;
        const targetState = !currentState;
        
        // If trying to remove permissions, check if any come from templates
        if (!targetState) {
            const permissionsFromTemplates = [];
            [funderPerm, repPerm, officerPerm].forEach(perm => {
                if (perm) {
                    const currentPermissions = userPermissions?.individual_permissions || [];
                    const hasDirectPermission = currentPermissions.some(p => p.id === perm.id);
                    const effectivePermissions = userPermissions?.effective_permissions || [];
                    const hasTemplatePermission = effectivePermissions.some(p => p.id === perm.id);
                    
                    // Check which templates provide this permission
                    const templatesWithPermission = (userPermissions?.permission_templates || []).filter(template => 
                        template.permissions.some(p => p.id === perm.id)
                    );
                    
                    if (hasTemplatePermission && !hasDirectPermission && templatesWithPermission.length > 0) {
                        permissionsFromTemplates.push({
                            permission: perm,
                            templates: templatesWithPermission
                        });
                    }
                }
            });
            
            // If any permissions come from templates, show alert and prevent removal
            if (permissionsFromTemplates.length > 0) {
                const groupNames = [...new Set(
                    permissionsFromTemplates.flatMap(item => item.templates.map(t => t.name))
                )].join(', ');
                
                setAlertData({
                    type: 'warning',
                    title: 'Cannot Remove Group Permissions',
                    message: `Some permissions for funder management come from the group: "${groupNames}". Remove the user from these groups first to remove the permissions.`
                });
                setShowAlert(true);
                return;
            }
        }
        
        // Create a new changes array by copying current pending changes
        let newChanges = [...pendingPermissionChanges];
        
        // Process all three permissions in a single batch
        [funderPerm, repPerm, officerPerm].forEach(perm => {
            if (perm) {
                // Remove any existing pending changes for this permission
                newChanges = newChanges.filter(change => 
                    !(change.type === 'permission' && change.permission_id === perm.id)
                );
                
                // Check current state of this permission
                const currentPermissions = userPermissions?.individual_permissions || [];
                const hasDirectPermission = currentPermissions.some(p => p.id === perm.id);
                const effectivePermissions = userPermissions?.effective_permissions || [];
                const hasTemplatePermission = effectivePermissions.some(p => p.id === perm.id);
                const currentlyHasPermission = hasDirectPermission || hasTemplatePermission;
                
                // Add appropriate change if needed
                if (targetState && !currentlyHasPermission) {
                    newChanges.push({
                        type: 'permission',
                        permission_id: perm.id,
                        action: 'add'
                    });
                } else if (!targetState && currentlyHasPermission) {
                    newChanges.push({
                        type: 'permission',
                        permission_id: perm.id,
                        action: 'remove'
                    });
                }
            }
        });
        
        // Update state once with all changes
        setPendingPermissionChanges(newChanges);
        setHasUnsavedChanges(newChanges.length > 0);
    }, [selectedUser, loading, getPermissionStatus, pendingPermissionChanges, userPermissions, setAlertData, setShowAlert]);





    // Filter user types relevant to BMO (exclude global admin types and students)
    const userTypes = [
        { value: '', label: 'All User Types' },
        { value: 'primary_representative', label: 'Primary Representative' },
        { value: 'secondary_representative', label: 'Secondary Representative' },
        { value: 'bmo_officer', label: 'BMO Officer' },
    ];

    return (
        <div className="bmo-user-permission-assignment">
            {showAlert && (
                <div className={`alert alert-${alertData.type}`}>
                    <div className="alert-content">
                        <h4>{alertData.title}</h4>
                        <p>{alertData.message}</p>
                        <button onClick={() => setShowAlert(false)}>Close</button>
                    </div>
                </div>
            )}

            <div className="assignment-container">
                <div className="user-panel">
                    <div className="filters">
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <select
                            value={userTypeFilter}
                            onChange={(e) => setUserTypeFilter(e.target.value)}
                        >
                            {userTypes.map(type => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                        <button 
                            onClick={testEmail}
                            className="test-email-btn"
                            style={{ marginLeft: '10px', padding: '5px 10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}
                        >
                            Test Email
                        </button>
                    </div>

                    <div className="user-list">
                        <h3>BMO Staff</h3>
                        {users.map(user => (
                            <div 
                                key={user.id} 
                                className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                                onClick={() => handleUserSelect(user)}
                            >
                                <div className="user-info">
                                    <strong>{user.email}</strong>
                                    <span className="user-type">{user.user_type.replace('_', ' ')}</span>
                                </div>
                                <button 
                                    className="view-user-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewUser(user);
                                    }}
                                >
                                    View
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="permission-panel">
                    {selectedUser ? (
                        <>
                            <div className="user-header">
                                <h3>Permissions for {selectedUser.email}</h3>
                                <div className="view-mode-switcher">
                                    <button 
                                        className={viewMode === 'templates' ? 'active' : ''}
                                        onClick={() => setViewMode('templates')}
                                    >
                                        Groups
                                    </button>
                                    <button 
                                        className={viewMode === 'permissions' ? 'active' : ''}
                                        onClick={() => setViewMode('permissions')}
                                    >
                                        Individual Permissions
                                    </button>
                                    <button 
                                        className={viewMode === 'funds' ? 'active' : ''}
                                        onClick={() => setViewMode('funds')}
                                    >
                                        Fund Restrictions
                                    </button>
                                </div>
                            </div>

                            {hasUnsavedChanges && (
                                <div className="unsaved-changes">
                                    <span>You have unsaved changes</span>
                                    <div className="action-buttons">
                                        <button onClick={saveChanges} disabled={loading}>
                                            {loading ? 'Saving...' : 'Save Changes'}
                                        </button>
                                        <button onClick={discardChanges} disabled={loading}>
                                            Discard
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="permission-content">
                                {loading ? (
                                    <div className="loading-spinner">Loading permissions...</div>
                                ) : (
                                    <>
                                        {viewMode === 'templates' && (
                                            <div className="templates-section">
                                                <h4>Permission Templates</h4>
                                                
                                                {/* Unified Funder Template Management */}
                                                {(() => {
                                                    console.log('🔍 Template Analysis:');
                                                    console.log('All templates:', templates.map(t => ({ name: t.name, desc: t.description })));
                                                    
                                                    return (
                                                        <>
                                                            {/* All Templates */}
                                                            {templates.length > 0 && (
                                                                <>
                                                                    <h6>Permission Groups</h6>
                                                                    <div className="checkbox-grid">
                                                                        {templates.map(template => (
                                                                            <label key={template.id} className="checkbox-item">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={getTemplateStatus(template.id)}
                                                                                    onChange={() => handleTemplateToggle(template.id)}
                                                                                />
                                                                                <span className="checkbox-label">
                                                                                    <strong>{template.name}</strong>
                                                                                    <small>{template.description}</small>
                                                                                </span>
                                                                            </label>
                                                                        ))}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        )}

                                        {viewMode === 'permissions' && (
                                            <div className="permissions-section">
                                                <h4>Individual Permissions</h4>
                                                
                                                
                                                {/* Unified Funder Management CRUD Table */}
                                                {(() => {
                                                    // Get all funder-related permissions from grouped permissions
                                                    const allFunderPermissions = groupedPermissions.flatMap(group => 
                                                        group.permissions.filter(p => {
                                                            const codename = p.codename || p.name?.toLowerCase();
                                                            return codename.includes('funder') && !codename.includes('funderapplication') &&
                                                                   !codename.includes('funderagreement') && !codename.includes('funderpayment');
                                                        })
                                                    );
                                                    
                                                    // Find the main funder permissions
                                                    const funderPerms = allFunderPermissions.filter(p => {
                                                        const codename = p.codename || p.name?.toLowerCase();
                                                        return codename.includes('funder') && !codename.includes('funderofficer') && 
                                                               !codename.includes('funderrepresentative') && !codename.includes('funderprimary') && 
                                                               !codename.includes('fundersecondary');
                                                    });
                                                    
                                                    return allFunderPermissions.length > 0 ? (
                                                        <div className="unified-permission-section">
                                                            <h6 className="unified-section-title">
                                                                <i className="fas fa-building me-2"></i>
                                                                Funder Management (Unified)
                                                            </h6>
                                                            
                                                            {/* CRUD Table */}
                                                            <div className="crud-table">
                                                                <div className="crud-table-header">
                                                                    <div className="crud-header-cell">Permission Type</div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-eye me-1"></i>
                                                                        View
                                                                    </div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-plus me-1"></i>
                                                                        Add
                                                                    </div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-edit me-1"></i>
                                                                        Change
                                                                    </div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-trash me-1"></i>
                                                                        Delete
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Single Unified Row */}
                                                                <div className="crud-table-row">
                                                                    <div className="crud-row-label">
                                                                        <i className="fas fa-building me-2"></i>
                                                                        <strong>Funder Management</strong>
                                                                        <small>Funders, Representatives & Officers</small>
                                                                    </div>
                                                                    
                                                                    {['view', 'add', 'change', 'delete'].map(action => {
                                                                        // Use funder permission as the master permission to show checkbox state
                                                                        const masterPermission = funderPerms.find(p => 
                                                                            (p.codename || p.name?.toLowerCase()) === `${action}_funder`
                                                                        );
                                                                        
                                                                        const isChecked = masterPermission ? getPermissionStatus(masterPermission.id) : false;
                                                                        
                                                                        return (
                                                                            <div key={action} className="crud-checkbox-cell">
                                                                                <label className="crud-checkbox">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={isChecked}
                                                                                        onChange={() => {
                                                                                            handleUnifiedFunderToggle(action, funderPerms, allFunderPermissions);
                                                                                        }}
                                                                                        disabled={!masterPermission}
                                                                                    />
                                                                                </label>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="crud-info">
                                                                <small className="text-muted">
                                                                    <i className="fas fa-info-circle me-1"></i>
                                                                    Each checkbox controls permissions for Funders, Representatives, and Officers together
                                                                </small>
                                                            </div>
                                                        </div>
                                                    ) : null;
                                                })()}
                                                
                                                {/* Bursary CRUD Table */}
                                                {(() => {
                                                    // Get all bursary permissions from grouped permissions
                                                    const allBursaryPermissions = groupedPermissions.flatMap(group => 
                                                        group.permissions.filter(p => {
                                                            const codename = p.codename || p.name?.toLowerCase();
                                                            return codename === 'view_bursary' || codename === 'add_bursary' || 
                                                                   codename === 'change_bursary' || codename === 'delete_bursary';
                                                        })
                                                    );
                                                    
                                                    return allBursaryPermissions.length > 0 ? (
                                                        <div className="unified-permission-section">
                                                            <h6 className="unified-section-title">
                                                                <i className="fas fa-award me-2"></i>
                                                                Bursaries
                                                            </h6>
                                                            
                                                            <div className="crud-table">
                                                                <div className="crud-table-header">
                                                                    <div className="crud-header-cell">Permission Type</div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-eye me-1"></i>
                                                                        View
                                                                    </div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-plus me-1"></i>
                                                                        Add
                                                                    </div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-edit me-1"></i>
                                                                        Change
                                                                    </div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-trash me-1"></i>
                                                                        Delete
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="crud-table-row">
                                                                    <div className="crud-row-label">
                                                                        <i className="fas fa-award me-2"></i>
                                                                        <strong>Bursaries</strong>
                                                                        <small>Scholarship & Financial Aid Programs</small>
                                                                    </div>
                                                                    
                                                                    {['view', 'add', 'change', 'delete'].map(action => {
                                                                        const permission = allBursaryPermissions.find(p => 
                                                                            (p.codename || p.name?.toLowerCase()) === `${action}_bursary`
                                                                        );
                                                                        
                                                                        const isChecked = permission ? getPermissionStatus(permission.id) : false;
                                                                        
                                                                        return (
                                                                            <div key={action} className="crud-checkbox-cell">
                                                                                <label className="crud-checkbox">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={isChecked}
                                                                                        onChange={() => {
                                                                                            if (permission) {
                                                                                                handlePermissionToggle(permission.id);
                                                                                            }
                                                                                        }}
                                                                                        disabled={!permission}
                                                                                    />
                                                                                </label>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="crud-info">
                                                                <small className="text-muted">
                                                                    <i className="fas fa-info-circle me-1"></i>
                                                                    Manage permissions for Bursary programs and financial aid
                                                                </small>
                                                            </div>
                                                        </div>
                                                    ) : null;
                                                })()}
                                                
                                                {/* Booking CRUD Table */}
                                                {(() => {
                                                    // Get all booking permissions from grouped permissions
                                                    const allBookingPermissions = groupedPermissions.flatMap(group => 
                                                        group.permissions.filter(p => {
                                                            const codename = p.codename || p.name?.toLowerCase();
                                                            return codename === 'view_voucherbooking' || codename === 'add_voucherbooking' || 
                                                                   codename === 'change_voucherbooking' || codename === 'delete_voucherbooking';
                                                        })
                                                    );
                                                    
                                                    return allBookingPermissions.length > 0 ? (
                                                        <div className="unified-permission-section">
                                                            <h6 className="unified-section-title">
                                                                <i className="fas fa-calendar-check me-2"></i>
                                                                Bookings
                                                            </h6>
                                                            
                                                            <div className="crud-table">
                                                                <div className="crud-table-header">
                                                                    <div className="crud-header-cell">Permission Type</div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-eye me-1"></i>
                                                                        View
                                                                    </div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-plus me-1"></i>
                                                                        Add
                                                                    </div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-edit me-1"></i>
                                                                        Change
                                                                    </div>
                                                                    <div className="crud-header-cell">
                                                                        <i className="fas fa-trash me-1"></i>
                                                                        Delete
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="crud-table-row">
                                                                    <div className="crud-row-label">
                                                                        <i className="fas fa-calendar-check me-2"></i>
                                                                        <strong>Bookings</strong>
                                                                        <small>Accommodation Booking Management</small>
                                                                    </div>
                                                                    
                                                                    {['view', 'add', 'change', 'delete'].map(action => {
                                                                        const permission = allBookingPermissions.find(p => 
                                                                            (p.codename || p.name?.toLowerCase()) === `${action}_voucherbooking`
                                                                        );
                                                                        
                                                                        const isChecked = permission ? getPermissionStatus(permission.id) : false;
                                                                        
                                                                        // Special behavior for different actions
                                                                        let isDisabled = !permission;
                                                                        let title = '';
                                                                        let className = '';
                                                                        
                                                                        if (action === 'add') {
                                                                            // Add is always grayed out - students can book openly
                                                                            isDisabled = true;
                                                                            title = 'Students can create bookings without special permissions';
                                                                            className = 'booking-add-disabled';
                                                                        } else if (action === 'view') {
                                                                            title = 'Controls if Bookings appears in sidebar navigation';
                                                                        } else if (action === 'change') {
                                                                            title = 'Allows accepting/rejecting bookings (BMO officers)';
                                                                        } else if (action === 'delete') {
                                                                            title = 'Dummy toggle - no functionality yet';
                                                                        }
                                                                        
                                                                        return (
                                                                            <div key={action} className="crud-checkbox-cell">
                                                                                <label className={`crud-checkbox ${className}`} title={title}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={action === 'add' ? true : isChecked}
                                                                                        onChange={() => {
                                                                                            if (permission && !isDisabled) {
                                                                                                handlePermissionToggle(permission.id);
                                                                                            }
                                                                                        }}
                                                                                        disabled={isDisabled}
                                                                                    />
                                                                                </label>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="crud-info">
                                                                <small className="text-muted">
                                                                    <i className="fas fa-info-circle me-1"></i>
                                                                    <strong>View:</strong> Controls sidebar visibility | 
                                                                    <strong>Add:</strong> Open to students | 
                                                                    <strong>Change:</strong> Accept/reject bookings | 
                                                                    <strong>Delete:</strong> Placeholder (no function)
                                                                </small>
                                                            </div>
                                                        </div>
                                                    ) : null;
                                                })()}
                                                
                                                {/* Regular permission groups (excluding representatives, officers, bursaries, bookings, and main funders) */}
                                                {/*groupedPermissions.map(group => {
                                                    // Filter out dummy permissions - only show implemented ones
                                                    const functionalPermissions = group.permissions.filter(permission => {
                                                        const codename = permission.codename || permission.name?.toLowerCase();
                                                        
                                                        // List of actually implemented permissions
                                                        const implementedPermissions = [
                                                            // User management
                                                            'view_user', 'delete_user',
                                                            // Funder management (handled in CRUD table)
                                                            'view_funder', 'add_funder', 'change_funder', 'delete_funder',
                                                            // Bursary management (handled in CRUD table)
                                                            'view_bursary', 'add_bursary', 'change_bursary', 'delete_bursary',
                                                            // Booking management (handled in CRUD table)
                                                            'view_voucherbooking', 'add_voucherbooking', 'change_voucherbooking', 'delete_voucherbooking',
                                                            // Funder officers (handled in CRUD table)
                                                            'view_funderofficer', 'add_funderofficer', 'change_funderofficer', 'delete_funderofficer',
                                                            // Funder representatives (handled in CRUD table)
                                                            'view_funderprimaryrepresentative', 'add_funderprimaryrepresentative', 'change_funderprimaryrepresentative', 'delete_funderprimaryrepresentative',
                                                            'view_fundersecondaryrepresentative', 'add_fundersecondaryrepresentative', 'change_fundersecondaryrepresentative', 'delete_fundersecondaryrepresentative',
                                                            // Funder applications
                                                            'view_funderapplication', 'add_funderapplication', 'change_funderapplication', 'delete_funderapplication',
                                                            // Funder agreements
                                                            'view_funderagreement', 'add_funderagreement', 'change_funderagreement', 'delete_funderagreement',
                                                            // Funder payments
                                                            'view_funderpayment', 'add_funderpayment', 'change_funderpayment', 'delete_funderpayment',
                                                            // Student management (basic)
                                                            'view_student', 'add_student', 'change_student'
                                                        ];
                                                        
                                                        return implementedPermissions.includes(codename);
                                                    });
                                                    
                                                    // Only show permissions that aren't in CRUD tables (representatives, officers, main funders, bursaries)
                                                    const otherPermissions = functionalPermissions.filter(permission => {
                                                        const codename = permission.codename || permission.name?.toLowerCase();
                                                        // Exclude permissions that have their own CRUD tables
                                                        return !codename.includes('representative') && 
                                                               !codename.includes('funderofficer') &&
                                                               !(codename === 'view_bursary' || codename === 'add_bursary' || 
                                                                 codename === 'change_bursary' || codename === 'delete_bursary') &&
                                                               !(codename === 'view_voucherbooking' || codename === 'add_voucherbooking' || 
                                                                 codename === 'change_voucherbooking' || codename === 'delete_voucherbooking') &&
                                                               !(codename.includes('funder') && !codename.includes('funderapplication') && 
                                                                 !codename.includes('funderagreement') && !codename.includes('funderpayment'));
                                                    });
                                                    
                                                    // Only show groups that have permissions not covered by CRUD tables
                                                    if (otherPermissions.length === 0) {
                                                        return null;
                                                    }
                                                    
                                                    return (
                                                        <div key={`${group.app_label}.${group.model_name || group.content_type || 'unknown'}`} className="permission-group">
                                                            <h5>{group.app_label} ({otherPermissions.length} functional permissions)</h5>
                                                            <div className="checkbox-grid">
                                                                {otherPermissions.map(permission => {
                                                                    const permissionStatus = getPermissionStatus(permission.id);
                                                                    
                                                                    return (
                                                                        <label key={permission.id} className="checkbox-item">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={permissionStatus}
                                                                                onChange={() => {
                                                                                    handlePermissionToggle(permission.id);
                                                                                }}
                                                                            />
                                                                            <span className="checkbox-label">
                                                                                <strong>{permission.name}</strong>
                                                                                <small>{permission.content_type__model}</small>
                                                                            </span>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                }).filter(Boolean)*/}
                                                
                                                {/* Show info about filtered permissions */}
                                                <div className="filtered-info">
                                                    <p className="text-muted small">
                                                        <i className="fas fa-info-circle me-2"></i>
                                                        Only showing functional permissions that actually control UI elements. 
                                                        Funder permissions are unified for easier management.
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {viewMode === 'funds' && (
                                            <div className="funds-section">
                                                <h4>Fund Access Restrictions</h4>
                                                
                                                <div className="fund-assignment-header">
                                                    <div className="fund-info">
                                                        <p className="text-muted">
                                                            <i className="fas fa-ban me-2"></i>
                                                            Manage fund restrictions for this user within your BMO. 
                                                            By default, users can access ALL BMO funds. Adding funds here RESTRICTS their access.
                                                        </p>
                                                    </div>
                                                    <button 
                                                        className="assign-funds-btn"
                                                        onClick={() => openFundModal(selectedUser)}
                                                        disabled={fundAssignmentLoading}
                                                    >
                                                        <i className="fas fa-ban me-2"></i>
                                                        Restrict Funds
                                                    </button>
                                                </div>

                                                <div className="assigned-funds-list">
                                                    {userFunds[selectedUser?.id] && userFunds[selectedUser.id].length > 0 ? (
                                                        <>
                                                            <h5>Currently Restricted Funds ({userFunds[selectedUser.id].length})</h5>
                                                            <div className="fund-cards">
                                                                {userFunds[selectedUser.id].map(fund => (
                                                                    <div key={fund.id} className="fund-card">
                                                                        <div className="fund-info">
                                                                            <div className="fund-header">
                                                                                <h6>{fund.name}</h6>
                                                                                <span className="fund-code">{fund.funder_code}</span>
                                                                            </div>
                                                                            <div className="fund-details">
                                                                                {fund.short_name && (
                                                                                    <span className="fund-short-name">{fund.short_name}</span>
                                                                                )}
                                                                                {fund.default_currency && (
                                                                                    <span className="fund-currency">
                                                                                        <i className="fas fa-coins me-1"></i>
                                                                                        {fund.default_currency}
                                                                                    </span>
                                                                                )}
                                                                                <span className="fund-bmo">
                                                                                    <i className="fas fa-building me-1"></i>
                                                                                    {fund.bmo_name}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <button 
                                                                            className="remove-fund-btn"
                                                                            onClick={() => removeFund(selectedUser.id, fund.id)}
                                                                            disabled={fundAssignmentLoading}
                                                                            title="Remove fund restriction"
                                                                        >
                                                                            <i className="fas fa-times"></i>
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="no-funds-assigned">
                                                            <div className="empty-state">
                                                                <i className="fas fa-check-circle fa-3x mb-3 text-success"></i>
                                                                <h5>No Fund Restrictions</h5>
                                                                <p className="text-muted">
                                                                    This user has access to ALL BMO funds by default. 
                                                                    Click "Restrict Funds" to limit their access to specific funds.
                                                                </p>
                                                                <button 
                                                                    className="assign-funds-btn primary"
                                                                    onClick={() => openFundModal(selectedUser)}
                                                                >
                                                                    <i className="fas fa-ban me-2"></i>
                                                                    Restrict First Fund
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="no-user-selected">
                            <h3>Select a user to manage their permissions</h3>
                            <p>Choose a user from the list on the left to view and edit their permission templates and individual permissions.</p>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .bmo-user-permission-assignment {
                    min-height: 100vh;
                    background-color: #f5f5f5;
                    padding: 20px;
                }

                .alert {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 1000;
                    max-width: 400px;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    animation: slideIn 0.3s ease-out;
                }

                .alert-success {
                    background-color: #d4edda;
                    border: 1px solid #c3e6cb;
                    color: #155724;
                }

                .alert-error {
                    background-color: #f8d7da;
                    border: 1px solid #f5c6cb;
                    color: #721c24;
                }

                .alert-warning {
                    background-color: #fff3cd;
                    border: 1px solid #ffeaa7;
                    color: #856404;
                }

                .alert-content {
                    padding: 15px;
                }

                .alert-content h4 {
                    margin: 0 0 5px 0;
                    font-size: 16px;
                }

                .alert-content p {
                    margin: 0 0 10px 0;
                    font-size: 14px;
                }

                .alert-content button {
                    background: transparent;
                    border: none;
                    color: inherit;
                    text-decoration: underline;
                    cursor: pointer;
                    font-size: 14px;
                }

                .assignment-container {
                    display: grid;
                    grid-template-columns: 400px 1fr;
                    gap: 20px;
                    height: calc(100vh - 40px);
                }

                .user-panel, .permission-panel {
                    background: white;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .filters {
                    padding: 20px;
                    background: #f8f9fa;
                    border-bottom: 1px solid #e9ecef;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .filters input, .filters select {
                    padding: 8px 12px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                }

                .user-list {
                    flex: 1;
                    overflow-y: auto;
                    padding: 0;
                }

                .user-list h3 {
                    padding: 20px 20px 0 20px;
                    margin: 0 0 15px 0;
                    color: #333;
                    font-size: 18px;
                }

                .user-item {
                    padding: 15px 20px;
                    border-bottom: 1px solid #f0f0f0;
                    cursor: pointer;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    transition: all 0.2s ease;
                }

                .user-item:hover {
                    background-color: #f8f9fa;
                }

                .user-item.selected {
                    background-color: #e3f2fd;
                    border-left: 4px solid #2196f3;
                }

                .user-info {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .user-info strong {
                    color: #333;
                    font-size: 14px;
                }

                .user-type {
                    color: #666;
                    font-size: 12px;
                    text-transform: capitalize;
                }

                .view-user-button {
                    background: #007bff;
                    color: white;
                    border: none;
                    padding: 6px 12px;
                    border-radius: 4px;
                    font-size: 12px;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .view-user-button:hover {
                    background: #0056b3;
                }

                .permission-panel {
                    display: flex;
                    flex-direction: column;
                }

                .user-header {
                    padding: 20px;
                    background: #f8f9fa;
                    border-bottom: 1px solid #e9ecef;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 15px;
                }

                .user-header h3 {
                    margin: 0;
                    color: #333;
                    font-size: 18px;
                }

                .view-mode-switcher {
                    display: flex;
                    background: white;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    overflow: hidden;
                }

                .view-mode-switcher button {
                    padding: 8px 16px;
                    border: none;
                    background: white;
                    color: #666;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 14px;
                }

                .view-mode-switcher button:hover {
                    background: #f8f9fa;
                }

                .view-mode-switcher button.active {
                    background: #4472C4;
                    color: white;
                }

                .unsaved-changes {
                    padding: 15px 20px;
                    background: #fff3cd;
                    border-bottom: 1px solid #ffeaa7;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 14px;
                    color: #856404;
                }

                .action-buttons {
                    display: flex;
                    gap: 10px;
                }

                .action-buttons button {
                    padding: 6px 12px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 12px;
                    transition: all 0.2s ease;
                }

                .action-buttons button:first-child {
                    background: #28a745;
                    color: white;
                }

                .action-buttons button:first-child:hover {
                    background: #218838;
                }

                .action-buttons button:last-child {
                    background: #6c757d;
                    color: white;
                }

                .action-buttons button:last-child:hover {
                    background: #5a6268;
                }

                .action-buttons button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .permission-content {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px;
                }

                .loading-spinner {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 200px;
                    color: #666;
                    font-size: 16px;
                }

                .no-user-selected {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    height: 100%;
                    text-align: center;
                    color: #666;
                }

                .no-user-selected h3 {
                    margin: 0 0 10px 0;
                    color: #333;
                }

                .no-user-selected p {
                    margin: 0;
                    max-width: 400px;
                    line-height: 1.5;
                }

                .templates-section h4, .permissions-section h4 {
                    margin: 0 0 20px 0;
                    color: #333;
                    font-size: 16px;
                    padding-bottom: 10px;
                    border-bottom: 2px solid #4472C4;
                }

                .permission-group {
                    margin-bottom: 30px;
                }

                .permission-group h5 {
                    margin: 0 0 15px 0;
                    color: #555;
                    font-size: 14px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 8px 12px;
                    background: #f8f9fa;
                    border-radius: 4px;
                }

                .checkbox-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 10px;
                }

                .checkbox-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    padding: 12px;
                    border: 1px solid #e9ecef;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    background: white;
                }

                .checkbox-item:hover {
                    border-color: #4472C4;
                    background: #f8f9fa;
                }

                .checkbox-item input[type="checkbox"] {
                    margin: 0;
                    width: 16px;
                    height: 16px;
                    accent-color: #4472C4;
                }

                .checkbox-label {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                    flex: 1;
                    min-width: 0;
                }

                .checkbox-label strong {
                    color: #333;
                    font-size: 14px;
                    line-height: 1.3;
                }

                .checkbox-label small {
                    color: #666;
                    font-size: 12px;
                    line-height: 1.2;
                }

                @keyframes slideIn {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }

                .filtered-info {
                    margin-top: 20px;
                    padding: 15px;
                    background: #f8f9fa;
                    border-radius: 6px;
                    border-left: 4px solid #17a2b8;
                }

                .filtered-info p {
                    margin: 0;
                    color: #666;
                    font-size: 13px;
                    line-height: 1.4;
                }

                .filtered-info .fas {
                    color: #17a2b8;
                }

                .unified-permission-section {
                    grid-column: 1 / -1;
                    margin: 20px 0;
                    padding: 20px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border-left: 4px solid #007bff;
                }

                .unified-section-title {
                    margin: 0 0 15px 0;
                    color: #007bff;
                    font-size: 16px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                }

                .unified-crud-row {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 15px;
                }

                .crud-table {
                    background: white;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    border: 1px solid #e9ecef;
                }

                .crud-table-header {
                    display: grid;
                    grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
                    background: linear-gradient(135deg, #007bff, #0056b3);
                    color: white;
                    font-weight: 600;
                    font-size: 14px;
                }

                .crud-header-cell {
                    padding: 15px 12px;
                    text-align: center;
                    border-right: 1px solid rgba(255,255,255,0.2);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                }

                .crud-header-cell:first-child {
                    text-align: left;
                    justify-content: flex-start;
                    background: rgba(0,0,0,0.1);
                }

                .crud-header-cell:last-child {
                    border-right: none;
                }

                .crud-table-row {
                    display: grid;
                    grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
                    border-bottom: 1px solid #e9ecef;
                    transition: background-color 0.2s ease;
                }

                .crud-table-row:hover {
                    background-color: #f8f9fa;
                }

                .crud-row-label {
                    padding: 20px 15px;
                    background: #f8f9fa;
                    border-right: 1px solid #e9ecef;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    gap: 4px;
                }

                .crud-row-label strong {
                    color: #2c3e50;
                    font-size: 16px;
                    font-weight: 600;
                }

                .crud-row-label small {
                    color: #6c757d;
                    font-size: 12px;
                    font-style: italic;
                }

                .crud-checkbox-cell {
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-right: 1px solid #e9ecef;
                    background: white;
                }

                .crud-checkbox-cell:last-child {
                    border-right: none;
                }

                .crud-checkbox {
                    position: relative;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .crud-checkbox input[type="checkbox"] {
                    width: 24px;
                    height: 24px;
                    margin: 0;
                    cursor: pointer;
                    accent-color: #007bff;
                    transform: scale(1.2);
                }

                .crud-checkbox input[type="checkbox"]:checked {
                    animation: checkboxPulse 0.3s ease;
                }

                @keyframes checkboxPulse {
                    0% { transform: scale(1.2); }
                    50% { transform: scale(1.4); }
                    100% { transform: scale(1.2); }
                }

                .crud-info {
                    margin-top: 12px;
                    padding: 10px 15px;
                    background: #e3f2fd;
                    border-radius: 6px;
                    border-left: 4px solid #2196f3;
                }

                .crud-info small {
                    color: #1565c0;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                /* Responsive Design for CRUD Table */
                @media (max-width: 768px) {
                    .crud-table-header,
                    .crud-table-row {
                        grid-template-columns: 1fr;
                    }
                    
                    .crud-header-cell:not(:first-child) {
                        display: none;
                    }
                    
                    .crud-row-label {
                        border-right: none;
                        border-bottom: 1px solid #e9ecef;
                        text-align: center;
                    }
                    
                    .crud-checkbox-cell {
                        display: none;
                    }
                    
                    .crud-table-row::after {
                        content: "📱 Mobile view: Use desktop for full CRUD controls";
                        grid-column: 1;
                        padding: 15px;
                        text-align: center;
                        color: #6c757d;
                        font-style: italic;
                        background: #f8f9fa;
                        font-size: 12px;
                    }
                }

                .unified-checkbox-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    padding: 12px;
                    background: white;
                    border-radius: 6px;
                    border: 2px solid #e9ecef;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .unified-checkbox-item:hover {
                    border-color: #007bff;
                    box-shadow: 0 2px 8px rgba(0,123,255,0.1);
                }

                .unified-checkbox-item input[type="checkbox"] {
                    margin: 0;
                    width: 18px;
                    height: 18px;
                    accent-color: #007bff;
                }

                .unified-checkbox-label {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                    flex: 1;
                }

                .unified-checkbox-label strong {
                    color: #2c3e50;
                    font-size: 14px;
                    font-weight: 600;
                }

                .unified-checkbox-label small {
                    color: #6c757d;
                    font-size: 12px;
                    font-style: italic;
                }

                @media (max-width: 1024px) {
                    .assignment-container {
                        grid-template-columns: 1fr;
                        grid-template-rows: auto 1fr;
                        height: auto;
                    }
                    
                    .user-panel {
                        max-height: 400px;
                    }
                    
                    .checkbox-grid {
                        grid-template-columns: 1fr;
                    }
                }

                @media (max-width: 768px) {
                    .bmo-user-permission-assignment {
                        padding: 10px;
                    }
                    
                    .user-header {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    
                    .unsaved-changes {
                        flex-direction: column;
                        gap: 10px;
                        align-items: flex-start;
                    }
                }

                /* Modal Styles - Office Document Theme */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.6);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                    padding: 20px;
                }

                .modal-content {
                    background: #ffffff;
                    border-radius: 2px;
                    max-width: 850px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
                    border: 1px solid #d1d5db;
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 24px 32px 16px;
                    border-bottom: 1px solid #e5e7eb;
                    background: #ffffff;
                    position: relative;
                }

                .modal-header h2 {
                    margin: 0;
                    color: #1f2937;
                    font-size: 24px;
                    font-weight: 600;
                    letter-spacing: -0.025em;
                }

                .close-button {
                    background: #f3f4f6;
                    border: 1px solid #d1d5db;
                    font-size: 18px;
                    cursor: pointer;
                    color: #6b7280;
                    padding: 8px;
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                }

                .close-button:hover {
                    background: #ef4444;
                    color: white;
                    border-color: #dc2626;
                }

                .modal-body {
                    padding: 32px;
                    background: #ffffff;
                    line-height: 1.6;
                }

                .user-details-section, .permissions-section {
                    margin-bottom: 40px;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                }

                .user-details-section h3, .permissions-section h3 {
                    margin: 0;
                    padding: 16px 24px;
                    color: #1f2937;
                    font-size: 18px;
                    font-weight: 600;
                    background: #f8f9fa;
                    border-bottom: 1px solid #e5e7eb;
                    display: flex;
                    align-items: center;
                }

                .user-details-section h3::before {
                    content: '●';
                    margin-right: 12px;
                    font-size: 12px;
                    color: #6b7280;
                    font-weight: normal;
                }

                .permissions-section h3::before {
                    content: '■';
                    margin-right: 12px;
                    font-size: 10px;
                    color: #6b7280;
                    font-weight: normal;
                }

                .detail-row {
                    display: grid;
                    grid-template-columns: 140px 1fr;
                    padding: 12px 24px;
                    border-bottom: 1px solid #f3f4f6;
                    align-items: center;
                    transition: background-color 0.15s ease;
                }

                .detail-row:hover {
                    background: #f9fafb;
                }

                .detail-row:last-child {
                    border-bottom: none;
                }

                .detail-label {
                    font-weight: 600;
                    color: #374151;
                    font-size: 14px;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }

                .detail-value {
                    color: #1f2937;
                    font-size: 15px;
                    font-weight: 500;
                    text-transform: capitalize;
                }

                .permission-groups, .individual-permissions {
                    padding: 16px 24px;
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 16px;
                }

                .permission-group-item {
                    background: #f8f9fa;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    padding: 16px;
                    transition: all 0.2s ease;
                    position: relative;
                }

                .permission-group-item::before {
                    content: '▪';
                    position: absolute;
                    top: 16px;
                    right: 16px;
                    font-size: 12px;
                    color: #9ca3af;
                }

                .permission-group-item:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
                    border-color: #d1d5db;
                }

                .permission-group-item strong {
                    color: #374151;
                    display: block;
                    margin-bottom: 8px;
                    font-size: 16px;
                    font-weight: 600;
                }

                .permission-group-item p {
                    margin: 0;
                    color: #475569;
                    font-size: 14px;
                    line-height: 1.5;
                }

                .permission-item {
                    background: #f3f4f6;
                    color: #374151;
                    padding: 8px 16px;
                    border-radius: 4px;
                    font-size: 13px;
                    font-weight: 500;
                    border: 1px solid #d1d5db;
                    display: inline-block;
                    margin: 4px;
                    transition: all 0.2s ease;
                }

                .permission-item:hover {
                    background: #e5e7eb;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }

                .permission-item.effective {
                    background: #f0fdf4;
                    color: #166534;
                    border: 1px solid #bbf7d0;
                }

                .permission-item.effective:hover {
                    background: #dcfce7;
                    box-shadow: 0 2px 4px rgba(34, 197, 94, 0.15);
                }

                /* Empty state styling */
                .permissions-section p {
                    padding: 24px;
                    text-align: center;
                    color: #6b7280;
                    font-style: italic;
                    background: #f9fafb;
                    margin: 0;
                    border-top: 1px solid #e5e7eb;
                }

                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .modal-content {
                        margin: 10px;
                        max-height: 95vh;
                    }
                    
                    .modal-body {
                        padding: 20px;
                    }
                    
                    .detail-row {
                        grid-template-columns: 1fr;
                        gap: 4px;
                        padding: 16px 20px;
                    }
                    
                    .detail-label {
                        font-size: 12px;
                        margin-bottom: 4px;
                    }
                    
                    .permission-groups, .individual-permissions {
                        grid-template-columns: 1fr;
                        padding: 16px 20px;
                    }
                }

                /* Fund Assignment Styles */
                .funds-section {
                    padding: 20px;
                }

                .fund-assignment-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 24px;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #e5e7eb;
                }

                .fund-info p {
                    margin: 0;
                    font-size: 14px;
                }

                .assign-funds-btn, .assign-funds-btn.primary {
                    background: #3b82f6;
                    color: white;
                    border: none;
                    padding: 10px 16px;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                }

                .assign-funds-btn:hover {
                    background: #2563eb;
                    transform: translateY(-1px);
                }

                .assign-funds-btn:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                    transform: none;
                }

                .fund-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 16px;
                    margin-top: 16px;
                }

                .fund-card {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 16px;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    transition: all 0.2s;
                }

                .fund-card:hover {
                    border-color: #3b82f6;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
                }

                .fund-info h6 {
                    margin: 0 0 8px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #1f2937;
                }

                .fund-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 8px;
                }

                .fund-code {
                    background: #f3f4f6;
                    color: #6b7280;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                    font-family: monospace;
                }

                .fund-details {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .fund-short-name, .fund-currency, .fund-bmo {
                    font-size: 13px;
                    color: #6b7280;
                    display: flex;
                    align-items: center;
                }

                .remove-fund-btn {
                    background: #ef4444;
                    color: white;
                    border: none;
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                    flex-shrink: 0;
                    margin-left: 12px;
                }

                .remove-fund-btn:hover {
                    background: #dc2626;
                    transform: scale(1.05);
                }

                .remove-fund-btn:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                    transform: none;
                }

                .no-funds-assigned {
                    padding: 40px 20px;
                    text-align: center;
                }

                .empty-state {
                    max-width: 400px;
                    margin: 0 auto;
                }

                .empty-state i {
                    color: #d1d5db;
                }

                .empty-state h5 {
                    margin: 16px 0 8px 0;
                    color: #374151;
                }

                .empty-state p {
                    margin: 8px 0 24px 0;
                    color: #6b7280;
                    line-height: 1.5;
                }

                /* Fund Modal Styles */
                .fund-modal {
                    max-width: 700px;
                    max-height: 80vh;
                    overflow-y: auto;
                }

                .fund-assignment-section {
                    padding: 0;
                }

                .assignment-info {
                    background: #f8fafc;
                    padding: 16px;
                    border-radius: 6px;
                    margin-bottom: 24px;
                }

                .fund-selection-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 16px;
                    padding-bottom: 12px;
                    border-bottom: 1px solid #e5e7eb;
                }

                .selection-actions {
                    display: flex;
                    gap: 8px;
                }

                .select-all-btn, .clear-selection-btn {
                    background: #f3f4f6;
                    color: #374151;
                    border: 1px solid #d1d5db;
                    padding: 6px 12px;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 13px;
                    transition: all 0.2s;
                }

                .select-all-btn:hover, .clear-selection-btn:hover {
                    background: #e5e7eb;
                }

                .select-all-btn:disabled, .clear-selection-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .fund-selection-list {
                    max-height: 400px;
                    overflow-y: auto;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                }

                .fund-selection-item {
                    border-bottom: 1px solid #f3f4f6;
                    transition: all 0.2s;
                }

                .fund-selection-item:last-child {
                    border-bottom: none;
                }

                .fund-selection-item.selected {
                    background: #eff6ff;
                    border-color: #bfdbfe;
                }

                .fund-selection-item.already-assigned {
                    background: #f9fafb;
                    opacity: 0.7;
                }

                .fund-checkbox {
                    padding: 0;
                }

                .fund-checkbox input[type="checkbox"] {
                    display: none;
                }

                .fund-checkbox label {
                    display: block;
                    padding: 16px;
                    cursor: pointer;
                    margin: 0;
                    width: 100%;
                }

                .fund-checkbox input[type="checkbox"]:disabled + label {
                    cursor: not-allowed;
                }

                .fund-selection-info {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .fund-main-info {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                }

                .fund-main-info h6 {
                    margin: 0;
                    font-size: 15px;
                    font-weight: 600;
                    color: #1f2937;
                }

                .already-assigned-badge {
                    background: #10b981;
                    color: white;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                }

                .fund-meta {
                    display: flex;
                    gap: 16px;
                    flex-wrap: wrap;
                }

                .fund-meta span {
                    font-size: 13px;
                    color: #6b7280;
                    display: flex;
                    align-items: center;
                }

                .modal-actions {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 24px;
                    padding-top: 16px;
                    border-top: 1px solid #e5e7eb;
                }

                .selection-summary {
                    font-size: 14px;
                    color: #6b7280;
                    font-weight: 500;
                }

                .action-buttons {
                    display: flex;
                    gap: 12px;
                }

                .cancel-btn {
                    background: #f3f4f6;
                    color: #374151;
                    border: 1px solid #d1d5db;
                    padding: 10px 16px;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    transition: all 0.2s;
                }

                .cancel-btn:hover {
                    background: #e5e7eb;
                }

                .assign-btn {
                    background: #3b82f6;
                    color: white;
                    border: none;
                    padding: 10px 16px;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                }

                .assign-btn:hover:not(:disabled) {
                    background: #2563eb;
                }

                .assign-btn:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                }

                .no-funds-available {
                    padding: 60px 20px;
                    text-align: center;
                }
            `}</style>

            {/* User Details Modal */}
            {showUserModal && modalUser && (
                <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>User Details</h2>
                            <button className="close-button" onClick={() => setShowUserModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="user-details-section">
                                <h3>Personal Information</h3>
                                <div className="detail-row">
                                    <span className="detail-label">Email:</span>
                                    <span className="detail-value">{modalUser.email || 'Not provided'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">First Name:</span>
                                    <span className="detail-value">{modalUser.first_name || 'Not provided'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Last Name:</span>
                                    <span className="detail-value">{modalUser.last_name || 'Not provided'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Phone:</span>
                                    <span className="detail-value">{modalUser.phone_number || 'Not provided'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">User Type:</span>
                                    <span className="detail-value">{modalUser.user_type?.replace('_', ' ') || 'Not specified'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Date Joined:</span>
                                    <span className="detail-value">{modalUser.date_joined ? new Date(modalUser.date_joined).toLocaleDateString() : 'Not available'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Last Login:</span>
                                    <span className="detail-value">{modalUser.last_login ? new Date(modalUser.last_login).toLocaleDateString() : 'Never'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Is Active:</span>
                                    <span className="detail-value">{modalUser.is_active ? 'Yes' : 'No'}</span>
                                </div>
                                {modalUser.title && (
                                    <div className="detail-row">
                                        <span className="detail-label">Title:</span>
                                        <span className="detail-value">{modalUser.title}</span>
                                    </div>
                                )}
                                {modalUser.bmo_name && (
                                    <div className="detail-row">
                                        <span className="detail-label">BMO:</span>
                                        <span className="detail-value">{modalUser.bmo_name}</span>
                                    </div>
                                )}
                                {modalUser.funder_name && (
                                    <div className="detail-row">
                                        <span className="detail-label">Funder:</span>
                                        <span className="detail-value">{modalUser.funder_name}</span>
                                    </div>
                                )}
                            </div>

                            <div className="permissions-section">
                                <h3>Permission Groups</h3>
                                {modalUser.permission_templates && modalUser.permission_templates.length > 0 ? (
                                    <div className="permission-groups">
                                        {modalUser.permission_templates.map(template => (
                                            <div key={template.id} className="permission-group-item">
                                                <strong>{template.name}</strong>
                                                <p>{template.description || 'No description'}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p>No permission groups assigned</p>
                                )}

                                <h3>Individual Permissions</h3>
                                {modalUser.individual_permissions && modalUser.individual_permissions.length > 0 ? (
                                    <div className="individual-permissions">
                                        {modalUser.individual_permissions.map((permission, index) => (
                                            <div key={permission.id || index} className="permission-item">
                                                {permission.name || permission.codename || permission}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p>No individual permissions assigned</p>
                                )}

                                <h3>Effective Permissions</h3>
                                {modalUser.effective_permissions && modalUser.effective_permissions.length > 0 ? (
                                    <div className="individual-permissions">
                                        {modalUser.effective_permissions.map((permission, index) => (
                                            <div key={permission.id || index} className="permission-item effective">
                                                {permission.name || permission.codename || permission}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p>No effective permissions</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Fund Assignment Modal */}
            {showFundModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowFundModal(false)}>
                    <div className="modal-content fund-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Restrict Fund Access for {selectedUser.email}</h2>
                            <button className="close-button" onClick={() => setShowFundModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="fund-assignment-section">
                                <div className="assignment-info">
                                    <p className="text-muted mb-3">
                                        <i className="fas fa-info-circle me-2"></i>
                                        Select funds from your BMO to assign access to this user. 
                                        Users can only access funds they are specifically assigned.
                                    </p>
                                </div>

                                {availableFunds.length > 0 ? (
                                    <>
                                        <div className="fund-selection-header">
                                            <h4>Available Funds ({availableFunds.length})</h4>
                                            <div className="selection-actions">
                                                <button 
                                                    className="select-all-btn"
                                                    onClick={() => setSelectedFunds(availableFunds.map(f => f.id))}
                                                    disabled={selectedFunds.length === availableFunds.length}
                                                >
                                                    Select All
                                                </button>
                                                <button 
                                                    className="clear-selection-btn"
                                                    onClick={() => setSelectedFunds([])}
                                                    disabled={selectedFunds.length === 0}
                                                >
                                                    Clear Selection
                                                </button>
                                            </div>
                                        </div>

                                        <div className="fund-selection-list">
                                            {availableFunds.map(fund => {
                                                const isSelected = selectedFunds.includes(fund.id);
                                                const isAlreadyAssigned = userFunds[selectedUser.id]?.some(af => af.id === fund.id);
                                                
                                                return (
                                                    <div 
                                                        key={fund.id} 
                                                        className={`fund-selection-item ${isSelected ? 'selected' : ''} ${isAlreadyAssigned ? 'already-assigned' : ''}`}
                                                    >
                                                        <div className="fund-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                id={`fund-${fund.id}`}
                                                                checked={isSelected}
                                                                disabled={isAlreadyAssigned}
                                                                onChange={(e) => handleFundSelection(fund.id, e.target.checked)}
                                                            />
                                                            <label htmlFor={`fund-${fund.id}`}>
                                                                <div className="fund-selection-info">
                                                                    <div className="fund-main-info">
                                                                        <h6>{fund.name}</h6>
                                                                        <span className="fund-code">{fund.funder_code}</span>
                                                                        {isAlreadyAssigned && (
                                                                            <span className="already-assigned-badge">
                                                                                <i className="fas fa-check-circle me-1"></i>
                                                                                Already Assigned
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="fund-meta">
                                                                        {fund.short_name && (
                                                                            <span className="fund-short-name">
                                                                                <i className="fas fa-tag me-1"></i>
                                                                                {fund.short_name}
                                                                            </span>
                                                                        )}
                                                                        {fund.default_currency && (
                                                                            <span className="fund-currency">
                                                                                <i className="fas fa-coins me-1"></i>
                                                                                {fund.default_currency}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="modal-actions">
                                            <div className="selection-summary">
                                                <span className="selected-count">
                                                    {selectedFunds.length} fund(s) selected
                                                </span>
                                            </div>
                                            <div className="action-buttons">
                                                <button 
                                                    className="cancel-btn"
                                                    onClick={() => setShowFundModal(false)}
                                                >
                                                    Cancel
                                                </button>
                                                <button 
                                                    className="assign-btn"
                                                    onClick={assignSelectedFunds}
                                                    disabled={selectedFunds.length === 0 || fundAssignmentLoading}
                                                >
                                                    {fundAssignmentLoading ? (
                                                        <>
                                                            <i className="fas fa-spinner fa-spin me-2"></i>
                                                            Assigning...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-check me-2"></i>
                                                            Assign {selectedFunds.length} Fund(s)
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="no-funds-available">
                                        <div className="empty-state">
                                            <i className="fas fa-wallet fa-3x mb-3 text-muted"></i>
                                            <h5>No Funds Available</h5>
                                            <p className="text-muted">
                                                There are no funds available in your BMO for assignment.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BMOUserPermissionAssignment;
